// The consolidated non-verification copy for a NOT-deflected answer.
//
// One status sentence explaining WHY PRIME will not stand behind the answer,
// and one provenance sentence carrying the stand-in / demonstration-content
// truth. Plain module (no JSX) so the Playwright journey specs can import the
// exact same derivation the page uses and assert correspondence with the
// server's decision rather than re-hardcoding sentences.

// Deliberately NARROW: only evidence-quality codes get a status phrase.
// Provenance facts (stand-in, demonstration grounding) belong to the
// provenance sentence below — mapping them here would say the same thing
// twice in adjacent lines. Unmapped codes fall back to the generic clause.
const NON_VERIFICATION_REASONS = Object.freeze({
  insufficient_citations: 'there were not enough usable citations',
  distinct_source_count_below_minimum: 'too few distinct sources supported the result',
  retrieval_empty: 'the search returned no usable evidence',
  retrieval_failed: 'the knowledge search did not complete',
  generation_incomplete: 'PRIME could not complete a grounded response',
});

export function describeNonVerificationStatus(reasons) {
  const explanations = [
    ...new Set(reasons.map((reason) => NON_VERIFICATION_REASONS[reason]).filter(Boolean)),
  ];
  const why = explanations.length
    ? explanations.join(' and ')
    : 'the available evidence did not meet PRIME’s verification requirements';
  return `PRIME could not verify an answer because ${why}; this is not verified PRIME guidance.`;
}

export function describeNonVerificationProvenance(answer, reasons) {
  const fallback =
    answer?.generation?.reason === 'provider_fallback' ||
    reasons.includes('provider_fallback_occurred');
  const demonstration =
    answer?.retrieval?.mode === 'seeded_local' ||
    reasons.includes('seeded_or_illustrative_grounding') ||
    reasons.includes('retrieval_not_live_kb');
  if (fallback && demonstration) {
    return 'PRIME used a stand-in response grounded in demonstration content, not a completed answer from the live PRIME knowledge base.';
  }
  if (fallback) {
    return 'PRIME used a stand-in response because a completed grounded answer was unavailable.';
  }
  if (demonstration) {
    return 'The result is grounded in demonstration content, not the live PRIME knowledge base.';
  }
  return 'The text below is an unverified result; check any sources shown before relying on it.';
}
